import { useEffect, useRef, useState, type ReactNode } from "react";
import { AssistantRuntimeProvider, ThreadPrimitive } from "@assistant-ui/react";
import type { AssistantType, ChatMessage } from "../../types/assistant";
import { useAssistantWebSocket } from "../../hooks/useAssistantWebSocket";
import { useAssistantRuntime } from "../../hooks/useAssistantRuntime";
import { ChatMessagesProvider } from "../../context/ChatMessagesContext";
import { AssistantTypeContext } from "./AssistantTypeContext";
import { AssistantUserMessage, AssistantAssistantMessage } from "./AssistantMessages";
import type { LiveModel } from "../../hooks/useAvailableModels";

/**
 * One model's column: its own socket, its own runtime, its own transcript.
 *
 * Deliberately has no composer. When several channels answer the same prompt, the
 * input belongs to the surface, not to any one column — so each channel exposes its
 * `send` upward on mount and the surface broadcasts to all of them.
 *
 * Each channel is genuinely independent: separate connection, separate history. That
 * is what makes them comparable, and also why they cannot share a previousResponseId.
 */
export function AssistantChannel({
  channelId,
  modelId,
  models,
  setModelId,
  onRemove,
  authToken,
  userEmail,
  assistantType,
  fileSearchFilters,
  registerSend,
  onTurnComplete,
  soloed,
  restoredMessages,
  emptyState,
  showHeader = true,
}: {
  channelId: string;
  modelId: string;
  models: LiveModel[];
  setModelId: (id: string) => void;
  onRemove?: () => void;
  authToken: string | null;
  userEmail: string;
  assistantType: AssistantType;
  fileSearchFilters: unknown;
  registerSend: (id: string, send: ((text: string) => void) | null) => void;
  onTurnComplete: (channelId: string, requestedModel: string, info: {
    model?: string;
    provider?: "openai" | "anthropic";
    inputTokens?: number;
    outputTokens?: number;
    elapsedMs: number;
    /** The exchange itself, so the surface can persist it. */
    prompt?: string;
    reply?: string;
    annotations?: unknown[];
  }) => void;
  /** True when this is the only channel, so it fills the width. */
  soloed: boolean;
  /** Prior turns to show when a saved thread is reopened. */
  restoredMessages?: ChatMessage[];
  /** What an empty thread shows. Defaults to the model label, which is what the
   *  comparison columns need; the home modal supplies its own greeting. */
  emptyState?: ReactNode;
  /**
   * The per-column header — model picker, connection dot, remove button. It only
   * makes sense where models are being compared. The home modal renders none of
   * it: nobody there chooses a model.
   */
  showHeader?: boolean;
}) {
  const { messages, setMessages, isRunning, sendMessage, cancel, isConnected } = useAssistantWebSocket({
    authToken,
    assistantType,
    fileSearchFilters: fileSearchFilters as never,
    model: modelId,
    onTurnComplete: (info) => {
      // Read the exchange off the transcript rather than tracking it separately —
      // the hook has already reconciled streaming deltas into final messages.
      const lastAssistant = [...messagesRef.current].reverse().find((m) => m.role === "assistant");
      const lastUser = [...messagesRef.current].reverse().find((m) => m.role === "user");
      onTurnComplete(channelId, modelId, {
        ...info,
        prompt: lastUser?.content,
        reply: lastAssistant?.content,
        annotations: lastAssistant?.annotations,
      });
    },
    onError: (error) => console.error(`[${modelId}] WebSocket error:`, error),
  });

  const runtime = useAssistantRuntime({ messages, setMessages, isRunning, sendMessage, cancel });

  // The completion callback is created before this render's messages exist, so it
  // reads them through a ref rather than closing over a stale array.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Reopening a saved thread drops its transcript in. Only while this channel is
  // still empty: overwriting a live conversation because a list refreshed would be
  // worse than not restoring at all.
  const restored = useRef<ChatMessage[] | null>(null);
  useEffect(() => {
    if (!restoredMessages || restoredMessages === restored.current) return;
    if (messagesRef.current.length > 0) return;
    restored.current = restoredMessages;
    setMessages(restoredMessages);
  }, [restoredMessages, setMessages]);

  // Hand our send upward so the shared composer can reach every channel at once.
  useEffect(() => {
    registerSend(channelId, sendMessage);
    return () => registerSend(channelId, null);
  }, [channelId, sendMessage, registerSend]);

  const label = models.find((m) => m.id === modelId)?.label ?? modelId;
  // The recommended subset by default, with the full catalogue a tick away — beside
  // the picker it filters, rather than in a top bar far from it. A model chosen
  // while ticked must stay listed after unticking, or the select would show one
  // value while holding another.
  const [showAll, setShowAll] = useState(false);
  const selected = models.find((m) => m.id === modelId);
  const forceShowAll = showAll || (selected != null && !selected.recommended);
  const visible = forceShowAll ? models : models.filter((m) => m.recommended);
  const hiddenCount = models.length - models.filter((m) => m.recommended).length;

  return (
    <AssistantTypeContext.Provider value={assistantType}>
      <AssistantRuntimeProvider runtime={runtime}>
        <ChatMessagesProvider messages={messages} userEmail={userEmail}>
          <div className={`flex min-h-0 min-w-0 flex-col ${soloed ? "flex-1" : "flex-1 border-r border-maven-gray-100 dark:border-maven-gray-800 last:border-r-0"}`}>
            {/* Per-column header: which model this is, and how to change or drop it */}
            {showHeader && (
            <div className="flex items-center gap-1 border-b border-maven-gray-100 dark:border-maven-gray-800 bg-maven-gray-100/50 dark:bg-maven-gray-950/60 px-2 py-1.5">
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                aria-label={`Model for column ${label}`}
                className="min-w-0 flex-1 truncate rounded border border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800 px-1.5 py-0.5 text-xs text-maven-gray-800 dark:text-maven-gray-100 focus:outline-none focus:ring-1 focus:ring-maven-teal-500"
              >
                {visible.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              <label
                className="flex shrink-0 cursor-pointer select-none items-center gap-1 text-[11px] text-maven-gray-500 dark:text-maven-gray-500"
                title={
                  hiddenCount > 0
                    ? `${hiddenCount} more: previous generations, premium and small tiers`
                    : "Show every model both providers expose"
                }
              >
                <input
                  type="checkbox"
                  checked={forceShowAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="accent-maven-teal-500"
                />
                Show all
              </label>
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${isConnected ? "bg-maven-teal-500" : "bg-maven-orange-500"}`}
                title={isConnected ? "Connected" : "Connecting…"}
              />
              {onRemove && (
                <button
                  onClick={onRemove}
                  aria-label={`Remove ${label}`}
                  className="shrink-0 px-1 text-maven-gray-500 dark:text-maven-gray-500 hover:text-maven-gray-800"
                >
                  ×
                </button>
              )}
            </div>
            )}

            <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col">
              <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-3 py-4">
                <ThreadPrimitive.Empty>
                  {emptyState ?? (
                    <div className="pt-6 text-center text-xs text-maven-gray-500 dark:text-maven-gray-500">{label}</div>
                  )}
                </ThreadPrimitive.Empty>
                <ThreadPrimitive.Messages
                  components={{ UserMessage: AssistantUserMessage, AssistantMessage: AssistantAssistantMessage }}
                />
              </ThreadPrimitive.Viewport>
            </ThreadPrimitive.Root>
          </div>
        </ChatMessagesProvider>
      </AssistantRuntimeProvider>
    </AssistantTypeContext.Provider>
  );
}
